import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { IMeetingFormProps } from "../../interfaces/admin/IMeetingForm";
import { postDataFromApi, putDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";
import { useNavigate } from "react-router-dom";
import { IMeetingReducerState } from "../../interfaces/store/IMeetingReducerState";
import { useSelector } from "react-redux";
import { ADMIN } from "../../constants/admin";
import { actionTypes } from "../../store/actionTypes/actionTypes";
import { IApiResponse } from "../../interfaces/common/IApiResponse";
import { useDispatch } from "react-redux";
import { MEETING_CATEGORIES } from "../../constants/meetingConstants";
import { SelectChangeEvent } from "@mui/material/Select";
import { COMMON } from "../../constants/common";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { IUserDetailsPermissionState } from "../../interfaces/store/IUserDetailsPermissionState";

dayjs.extend(utc);

const MeetingForm: React.FC<IMeetingFormProps> = ({ handleDialogClose}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [touchedFields, setTouchedFields] = useState({
    from_date: false,
  });

  const selectedMeetingData = useSelector(  
    (state: { meeting: IMeetingReducerState }) =>
      state.meeting.selectedMeetingData
  );
  const meetingFormType = useSelector(
    (state: { meeting: IMeetingReducerState }) => state.meeting.meetingFormType
  );
  const userDetails = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.userDetails
  );

  // Filter categories based on pbacAccessAllowed
  const getAvailableCategories = () => {
    const pbacAccessAllowed = userDetails?.pbacAccessAllowed || false;
    if (pbacAccessAllowed) {
      return MEETING_CATEGORIES; // Show all categories including PBAC
    } else {
      // Exclude PBAC category
      const { PBAC, ...otherCategories } = MEETING_CATEGORIES;
      return otherCategories;
    }
  };

  const [formData, setFormData] = useState({
    id:"",
    title: "",
    meeting_url: "",
    agenda: "",
    category: "",
    from_date: null as Dayjs | null,
    to_date: null as Dayjs | null,
    start_time: null as Dayjs | null,
    end_time: null as Dayjs | null,
    tag: "",
    fullday: false,
    meeting_id: null,
  });
  
  const [formLoading, setFormLoading] = useState(false);

  const handleChange = (field: string, value: any) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
    if (field === "fullday") {
      const updatedFormData = {
        ...formData,
        fullday: value,
      };

      if (value) {
        const today = dayjs();
        updatedFormData.from_date = today.startOf('day');
        updatedFormData.to_date = today.endOf('day');
      }

      setFormData(updatedFormData);
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
      
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    handleChange(name, value);
  };

  const getMeetingDetails = async () => {
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.MEETING_LIST}`;
    const payloadData = {};
    dispatch({
      type: actionTypes.UPDATE_MEETING_FORM_LOADING,
      payload: true,
    });
    await postDataFromApi<any>(
      url,
      payloadData,
      (response: IApiResponse<any>) => {
        dispatch({
          type: actionTypes.UPDATE_MEETING_TABLE_DATA,
          payload: response.data,
        });
        dispatch({
          type: actionTypes.UPDATE_MEETING_FORM_LOADING,
          payload: false,
        });
      },
      { isAuthRequired: true },
      navigate
    );
    dispatch({
      type: actionTypes.UPDATE_MEETING_FORM_LOADING,
      payload: false,
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (meetingFormType === ADMIN.MEETING_FORM_TYPE.EDIT) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  const toUTCISOStringWithTime = (date: Dayjs | null): string => {
    if (!date) return new Date().toISOString();
    return date.toISOString();
  };
  
  const extractUTCTimeFromDate = (date: Dayjs | null): string => {
    if (!date) return "00:00";
    const utcDate = dayjs.utc(date);
    const hours = utcDate.hour().toString().padStart(2, '0');
    const minutes = utcDate.minute().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  const handleCreate = async () => {
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.CREATE_MEETING}`;
  
    const payloadData = {
      ...formData,
      from_date: toUTCISOStringWithTime(formData.from_date),
      to_date: toUTCISOStringWithTime(formData.to_date),
      start_time: extractUTCTimeFromDate(formData.from_date),
      end_time: extractUTCTimeFromDate(formData.to_date),
    };

    console.log("Payload to send (in UTC):", payloadData);
    setFormLoading(true);
  
    await postDataFromApi<any>(
      url,
      payloadData,
      () => {
        handleDialogClose();
        getMeetingDetails();
        setFormLoading(false);
        dispatch({
          type: COMMON.SHOW_SNACKBAR,
          payload: {
            message: "Meeting created successfully.",
            severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
          },
        });
      },
      { isAuthRequired: true },
      navigate
    );
  
    setFormLoading(false);
  };
  
  const handleUpdate = async () => {
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.UPDATE_MEETING}`;
    const payloadData = {
      ...formData,
      from_date: toUTCISOStringWithTime(formData.from_date),
      to_date: toUTCISOStringWithTime(formData.to_date),
      start_time: extractUTCTimeFromDate(formData.from_date),
      end_time: extractUTCTimeFromDate(formData.to_date), 
      id: String(formData.id),
    };
    setFormLoading(true);
    await putDataFromApi<any>(
      url,
      payloadData,
      () => {
        handleDialogClose();
        getMeetingDetails();
        setFormLoading(false);
        dispatch({
          type: COMMON.SHOW_SNACKBAR,
          payload: {
            message: "Meeting updated successfully.",
            severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
          },
        });
      },
      { isAuthRequired: true },
      navigate
    );
    setFormLoading(false);
  };

  // Combine and treat as UTC, then convert to local time
  const toLocalDateTime = (dateStr: string, timeStr: string): Dayjs => {    
    return dayjs(`${dateStr}T${timeStr}Z`);
  };
  
  useEffect(() => {
    if (selectedMeetingData?.from_date && selectedMeetingData?.start_time) {
      const localFromDate = toLocalDateTime(selectedMeetingData.from_date, selectedMeetingData.start_time);
      const localToDate = toLocalDateTime(selectedMeetingData.to_date, selectedMeetingData.end_time);
      setFormData({
        ...selectedMeetingData,
        from_date: localFromDate,
        to_date: localToDate
      });
    }
  }, [selectedMeetingData]); 
  
  const handleFromDateChange = (newFromDate: unknown) => {
    const date = newFromDate as Dayjs | null;
    if (!date) return;
  
    setFormData((prev) => {
      const toDate = formData.fullday
        ? date.endOf('day')
        : prev.to_date;
  
      return {
        ...prev,
        from_date: formData.fullday
          ? date.startOf('day')
          : date,
        to_date: toDate,
      };
    });
  };
  
  const handleToDateChange = (newToDate: unknown) => {
    const date = newToDate as Dayjs | null;
    if (!date) return;
  
    setFormData((prev) => {
      const fromDate = formData.fullday
        ? date.startOf('day')
        : prev.from_date;
  
      return {
        ...prev,
        to_date: formData.fullday
          ? date.endOf('day')
          : date,
        from_date: fromDate,
      };
    });
  };
  
  return (
    <>
      <form id='login-form' onSubmit={handleSubmit}>
        <Grid container rowSpacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} sx={{ pr: 2 }}>
            <TextField
              required
              label='Title'
              variant='outlined'
              fullWidth
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id='category-label'>Category</InputLabel>
              <Select
                labelId='category-label'
                id='category'
                name='category'
                value={formData.category}
                label='Category'
                onChange={handleSelectChange}
                disabled={meetingFormType === ADMIN.MEETING_FORM_TYPE.EDIT}
              >
                {Object.entries(getAvailableCategories()).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container rowSpacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              label='Meeting ID'
              required
              variant='outlined'
              fullWidth
              value={formData.meeting_id}
              onChange={(e) =>
                handleChange("meeting_id", e.target.value?.replace(/\s+/g, ""))
              }
              disabled={meetingFormType === ADMIN.MEETING_FORM_TYPE.EDIT}
            />
          </Grid>
        </Grid>
        <TextField
          label='Meeting URL'
          variant='outlined'
          sx={{ mb: 2 }}
          fullWidth
          value={formData.meeting_url}
          onChange={(e) => handleChange("meeting_url", e.target.value)}
          disabled={meetingFormType === ADMIN.MEETING_FORM_TYPE.EDIT}
        />
        <Grid container rowSpacing={2}>
          <Grid item xs={10} sx={{ pr: 2 }}>
            <TextField
              label='Tag'
              variant='outlined'
              fullWidth
              value={formData.tag}
              onChange={(e) => handleChange("tag", e.target.value)}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.fullday}
                  onChange={(e) => handleChange("fullday", e.target.checked)}
                />
              }
              label='All Day'
            />
          </Grid>
        </Grid>
        <TextField
          label='Description'
          variant='outlined'
          fullWidth
          sx={{ my: 2 }}
          value={formData.agenda}
          onChange={(e) => handleChange("agenda", e.target.value)}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container rowSpacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} sx={{ pr: 2 }}>             
              <DateTimePicker
                label="From Date"
                value={formData.from_date}
                onChange={handleFromDateChange}
                onAccept={(newValue) => {
                  const date = newValue as Dayjs | null;
                  if (date && date.isBefore(dayjs())) {
                    // Reject invalid past selection
                    setFormData((prev) => ({ ...prev, from_date: null }));                  
                  }
                }}
                ampm={false}
                shouldDisableTime={(_, clockType) =>
                  formData.fullday && (clockType === 'hours' || clockType === 'minutes')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    error={touchedFields.from_date && !formData.from_date}
                    helperText={touchedFields.from_date && !formData.from_date ? "From Date is required" : ""}
                  />
                )}
                disablePast
                minDateTime={dayjs()}                
              />
            </Grid>
            <Grid item xs={12} sm={6}>
            <DateTimePicker
                label="To Date"
                value={formData.to_date}
                onChange={handleToDateChange}
                onAccept={(newValue) => {
                  const date = newValue as Dayjs | null;
                  const minAllowed = formData.from_date ? formData.from_date.add(1, 'minute') : dayjs();
                  if (date && date.isBefore(minAllowed)) {
                    // Reject invalid selection
                    setFormData((prev) => ({ ...prev, to_date: null }));
                  }
                }}
                ampm={false}
                shouldDisableTime={(_, clockType) =>
                  formData.fullday && (clockType === 'hours' || clockType === 'minutes')
                }
                minDate={formData.from_date}
                disabled={!formData.from_date}
                minTime={formData.fullday ? undefined : formData.from_date}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    error={touchedFields.from_date && !formData.from_date}
                    helperText={touchedFields.from_date && !formData.from_date ? "From Date is required" : ""}
                  />
                )}
                disablePast    
                minDateTime={
                  formData.from_date
                    ? formData.from_date.add(1, 'minute')
                    : dayjs()
                }
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant='outlined' onClick={handleDialogClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          {meetingFormType === ADMIN.MEETING_FORM_TYPE.EDIT ? (
            <Button variant='contained' type='submit' disabled={formLoading}>
              UPDATE
            </Button>
          ) : (
            <Button variant='contained' type='submit' disabled={formLoading}>
              Create
            </Button>
          )}
        </Box>
      </form>
    </>
  );
};

export default MeetingForm;
